/** Backend configuration (all durations in milliseconds). */
import type { FeatureFlag } from '../../shared/protocol/messages';
import { APP_VERSION } from '../../shared/protocol/version';
import { CLAN_EMBLEMS } from '../../shared/clan-rules';
import { ASSAULT_ZONE_PAYMENTS } from './game-rules';

export type TrustLevel = 'L0' | 'L1' | 'L2';

export interface BackendConfig {
  /**
   * L0 = trust the client; L1 = range/monotonicity validation of saves; L2 = authoritative:
   * the server owns the game state (profile.load creates and enters it, the game changes it only through
   * player.command) and refuses profile.save / profile.importLegacy except the enabled import into a fresh profile.
   * At L0/L1 the client owns its state (it creates it, runs the session entry and saves it); commands work there too.
   * Default L2 for both the self-hosted server and the in-process (offline) backend; L0/L1 stay for tests and tools.
   */
  trustLevel: TrustLevel;
  /**
   * L2: accept the import of a local or legacy save into a FRESH profile (ProfileService isFreshProfile):
   * profile.importLegacy and profile.save { reason: 'import' }. L0/L1 always accept imports.
   */
  allowLegacyImport: boolean;
  /** profile.load { dev: true } may create the developer test profile (5 000 000 gold, every weapon) here. */
  devProfiles: boolean;
  commands: {
    /** Responses of the last N player.command opIds kept in the profile document (retries after a lost reply). */
    journalSize: number;
  };
  auth: {
    /**
     * Lifetime of an access token (short; the client gets a new one with auth.refresh). Never longer than the session's
     * refresh token.
     */
    accessTtlMs: number;
    /**
     * Lifetime of a refresh token. Every auth.refresh issues a new one, so a device session ends after this long without
     * any refresh (the protocol 1 session token lifetime, TOKEN_TTL, maps here).
     */
    refreshTtlMs: number;
    /** Device sessions per account; signing in on one more device ends the least recently used session. */
    maxSessionsPerAccount: number;
    /**
     * One active session per account (default true): a sign-in, or a resume / refresh of another session of the
     * account, ends every other session of it (reason 'replaced': their WebSockets close with code 4004, their tokens
     * answer UNAUTHORIZED 'session_replaced'). false: up to maxSessionsPerAccount sessions live side by side.
     */
    singleSession: boolean;
    /**
     * After a rotation, the previous refresh token is answered for this long with the same new pair (a retry whose
     * reply was lost, or another window of the same device refreshing at the same moment) instead of counting as a
     * reuse; later it revokes the session (theft detection). 0 = every reuse revokes.
     */
    refreshReuseGraceMs: number;
    /**
     * A retry of an auth.refresh whose reply was lost (the SAME request id with the previous refresh token) is answered
     * for this long after the rotation, also after a server restart: with the same pair while the server remembers it,
     * else with a new pair of the same generation (the lost one is dropped). Longer than a client's request timeout
     * times its retries. 0 = only the reuse grace window above applies.
     */
    refreshRetryWindowMs: number;
    /**
     * Prefix of the default display names of new guests: `<prefix>-XXXX` with 4 random base-36 characters, unique per
     * backend (the original falls back to "Игрок", FWP.as:746-750).
     */
    defaultDisplayName: string;
    /** Failed password checks per nickname / login key in loginFailureWindowMs (then RATE_LIMITED). */
    maxLoginFailures: number;
    /**
     * Failed password checks per client address (IPv6: its /64) in loginFailureWindowMs, whatever the names: one
     * common password tried against many public nicknames. 0 = no per-address limit (the in-process backend has no
     * addresses anyway).
     */
    maxLoginFailuresPerAddress: number;
    loginFailureWindowMs: number;
  };
  leaderboard: { pageSize: number; maxPages: number };
  assault: {
    zoneCount: number;
    payments: readonly number[];
    tributeIntervalMs: number;
    /** Clan members receive payments / clanShareDivisor of their clanmates' zones (FWP.as:21041-21077). */
    clanShareDivisor: number;
    /** Require a finished, verified battle session to capture a zone. */
    requireVerifiedBattle: boolean;
  };
  /**
   * The player-made clans (services/clans.ts; the name rules: src/shared/clan-rules.ts). system.hello publishes the
   * numbers as limits.clans.
   */
  clans: {
    /** clan.create needs a registered account (not a guest) of at least this level (CLAN_MIN_LEVEL). */
    minLevelToCreate: number;
    /** One clan per player per this period. */
    createCooldownMs: number;
    /** A leader renames the clan at most once per this period (operators any time). */
    renameCooldownMs: number;
    /**
     * After leaving or being kicked, a player joins a clan again only after this period: nobody hops from clan to clan
     * for the tribute of their zones.
     */
    rejoinCooldownMs: number;
    maxMembers: number;
    maxOfficers: number;
    /** Pending requests at once: of one player, of one clan. */
    maxRequestsPerPlayer: number;
    maxRequestsPerClan: number;
    /** Events kept in a clan's history (the oldest go first). */
    historyKept: number;
    /** The emblems a clan may choose (frames of the game's clan_icons sprite). */
    emblems: readonly number[];
    /**
     * Words no clan name or description may contain besides the built-in reserved words (CLAN_NAME_DENYLIST_FILE: one
     * word or phrase per line; compared like clan names).
     */
    denylist: readonly string[];
  };
  /**
   * The chat (services/chat.ts; the text rules: src/shared/chat-rules.ts). system.hello publishes the numbers as
   * limits.chat.
   */
  chat: {
    /** Messages each channel keeps (the oldest go first): the global channel, a clan's channel. */
    historyGlobal: number;
    historyClan: number;
    /** Writing needs a registered account (guests read only) of at least this level (CHAT_MIN_LEVEL). */
    minLevelToSend: number;
    /** At most `burst` messages of one player per `windowMs`, at least `minIntervalMs` apart. */
    burst: number;
    windowMs: number;
    minIntervalMs: number;
    /** The same text (compared like the flood key of chat-rules) again within this period is refused. */
    duplicateWindowMs: number;
    /**
     * At most this many messages per `windowMs` from one client address (IPv6: its /64), all its players together:
     * several accounts of one spammer (CHAT_ADDRESS_BURST; 0: no such limit).
     */
    addressBurst: number;
    /** A subscription of a player who has been offline this long ends (a reconnect within it keeps it). */
    subscriptionGraceMs: number;
    /** The longest mute an operator sets. */
    maxMuteMs: number;
    /** Words masked with `*` in every message (CHAT_WORD_FILTER_FILE: one word or phrase per line). */
    words: readonly string[];
  };
  /**
   * The annihilator auction (design D7: the original "king of the hill", paid in diamonds; services/auction.ts).
   * `startPrice`: diamonds of the first purchase of a round (AUCTION_START_PRICE; the original's last known price was
   * 20 VK votes). `roundMs`: a holder nobody outbids for this long keeps a copy for good and a new round starts (the
   * original: seven days). `winnersShown`: winners of earlier rounds that auction.get lists (newest first).
   */
  auction: { startPrice: number; roundMs: number; winnersShown: number };
  replays: { maxPerPlayer: number };
  /**
   * Retention of the stored ended battles (collection battles, services/battle-archive.ts): the core's sweep deletes
   * the ones that ended more than `keepMs` ago (BATTLES_KEEP) and the oldest beyond `maxStored` (BATTLES_MAX). The
   * owner's replay keeps the battle's log.
   */
  battles: { keepMs: number; maxStored: number };
  matchmaking: { levelBucketSize: number; widenAfterMs: number; ticketTtlMs: number };
  rooms: { lobbyTtlMs: number; chatBurst: number; chatWindowMs: number };
  sessions: {
    /** Extra wall-clock time on top of TURNTIME before the backend force-ends a turn. */
    turnGraceSec: number;
    finishGraceMs: number;
    soloTtlMs: number;
    /** Solo sessions (session.start) one player may run at once. */
    maxSoloPerPlayer: number;
    /** session.start per player: `burst` at once, then `perHour` (every ended session is stored). */
    soloStarts: { burst: number; perHour: number };
    keepEndedMs: number;
    maxHashTicksKept: number;
    /**
     * A player who lost its last connection keeps its lobby slot and ready flag, and its battle turns are not forced,
     * for this long (DISCONNECT_GRACE); after a server restart every player gets this long to come back.
     */
    disconnectGraceMs: number;
    /** Active sessions with only input changes (no turn change) are written at most this often (and at shutdown). */
    persistIntervalMs: number;
    /** Wall-clock budget of the re-simulation of a finished online battle. */
    simulationTimeoutMs: number;
  };
  /** Per client (token, WebSocket connection, or IP for anonymous requests). */
  rateLimit: { enabled: boolean; burst: number; perSecond: number; accountsPerIpBurst: number; accountsPerIpPerHour: number };
  /**
   * Replies of mutations kept for retries under the same request id (backend.ts): for `ttlMs`, at most `maxEntries`.
   * `persist`: the replies are also stored (collection `idempotency`, sealed), so a retry after a server restart gets
   * the same reply instead of running the mutation again (default on for the server, off in-process).
   */
  idempotency: { ttlMs: number; maxEntries: number; maxBytes: number; persist: boolean };
  /** Interval of the background sweeper (auction rounds, turn deadlines, mailboxes). */
  sweepIntervalMs: number;
  /** The public API as clients of every platform see it (system.hello, system.describe; design D9). */
  api: {
    /**
     * The software version this server reports (system.hello, system.describe, GET /health): the self-hosted server's
     * package.json version; default APP_VERSION of the build.
     */
    serverVersion: string;
    /** Oldest client version system.hello accepts (older: VERSION_MISMATCH 'client_too_old'); null = any version. */
    minClientVersion: string | null;
    /** The server's public base URL (`https://fwp.example.org`), reported to clients; null = not configured. */
    publicBaseUrl: string | null;
    /** Optional features switched off (OPTIONAL_FEATURES); their requests get FORBIDDEN 'feature_disabled'. */
    disabledFeatures: readonly FeatureFlag[];
  };
  /**
   * Per-player event replay buffer: the last `maxEvents` events of a player, each kept for `retentionMs`, so a client
   * that reconnects (not after a restart) gets what it missed (events.resume / events.poll) or an explicit gap.
   */
  events: { maxEvents: number; retentionMs: number };
  /**
   * In-app purchases (services/store.ts; the products: ../monetization-catalog.ts). The provider 'google-play' is on
   * when the server has Google Play credentials (BackendCoreOptions.googlePlay, PLAY_SERVICE_ACCOUNT_FILE).
   */
  store: {
    /**
     * TEST / DEVELOPMENT SWITCH (STORE_DEV_TEST): the provider 'dev-test' credits a purchase whose token starts with
     * 'devtest.' without asking any store. Never on a server that players use (warned at start). Default: on in-process
     * (the offline backend credits only its own local state), off on a server.
     */
    devTest: boolean;
    /** How often the voided purchases of Google Play are fetched (PLAY_VOIDED_POLL; 0 = never). */
    voidedPollMs: number;
    /** A credited purchase whose server-side consume failed is tried again after this long. */
    consumeRetryMs: number;
  };
  /** Opt-in rewarded ads (services/ads.ts; the placements: ../monetization-catalog.ts). */
  ads: {
    /** AdMob server-side verification callbacks are accepted (ADMOB_SSV): the provider 'admob'. */
    admob: boolean;
    /** TEST / DEVELOPMENT SWITCH (ADS_DEV_TEST): the provider 'dev-test' (ads.testReward). Default: on in-process, off on a server. */
    devTest: boolean;
    /** A ticket not verified within this long expires. */
    ticketTtlMs: number;
    /** Open tickets of one player at once. */
    maxOpenTickets: number;
    /** Ended tickets are kept (for the audit and repeated callbacks) this long. */
    keepTicketsMs: number;
  };
  /** The audit log of purchases and ad rewards (collection monetization_audit): kept by count and age. */
  monetizationAudit: { maxEntries: number; maxAgeMs: number };
  /**
   * E-mail sign-in (services/email.ts): on when the backend has a mail sender (BackendCoreOptions.emailSender,
   * EMAIL_PROVIDER), password accounts and `linkBaseUrl`.
   */
  email: {
    /**
     * The site of the account links (EMAIL_LINK_BASE_URL, default PUBLIC_BASE_URL): https://<site>/link/?a=verify#t=<token>
     * (the page public/link/, and the app through Android App Links). null = no links, so no e-mail sign-in.
     */
    linkBaseUrl: string | null;
    /** How long a confirmation link of an address works. */
    verifyTtlMs: number;
    /** How long a password reset link works. */
    resetTtlMs: number;
    /** Mails one account may cause per hour (confirmations, resends); more: RATE_LIMITED 'email_send_limit'. */
    sendsPerAccountPerHour: number;
    /** Mails one address may receive per hour (silently skipped beyond: no answer tells an address apart). */
    sendsPerAddressPerHour: number;
    /**
     * Security notices (email_changing, password_changed) one address may receive per hour: a budget of their own, so
     * the mails anybody can cause to that address (reset links, sign-ups with it) never crowd them out. They go only to
     * an account's own confirmed address after its password or one of its reset links was used.
     */
    noticesPerAddressPerHour: number;
    /** E-mail requests without a session (password reset) per client address per hour. */
    requestsPerClientPerHour: number;
  };
  /** The audit log of account security events (collection account_audit, services/account-audit.ts). */
  accountAudit: { maxEntries: number; maxAgeMs: number };
  /** Play Integrity (services/integrity.ts): on when the backend has a verifier (PLAY_INTEGRITY). */
  integrity: { challengeTtlMs: number; maxTokenAgeMs: number };
  /** Push notifications (services/push.ts): on when the backend has a sender (FCM_SERVICE_ACCOUNT_FILE). */
  push: { maxDevicesPerAccount: number; testIntervalMs: number };
  /**
   * The server-side anti-cheat (services/anticheat.ts, the heuristics: services/anticheat-rules.ts): it only MARKS
   * players for the operators (console: players, anticheat); it never blocks, bans or changes anything a player has.
   * The thresholds and why they are where they are: docs/security.md. ANTICHEAT_CONFIG_FILE (a JSON file with a part of
   * this object) overrides them on the self-hosted server.
   */
  anticheat: AnticheatConfig;
}

/** Gold and diamonds (an amount, a threshold). */
export interface CurrencyPair {
  gold: number;
  diam: number;
}

export interface AnticheatConfig {
  /** Default: on for the server, off in-process (the offline backend has no operators). */
  enabled: boolean;
  /**
   * The reconciliation job: every `intervalMs` it compares the stored balance of the players written since its last
   * run, plus `batch` more profiles of a rotating full pass (one pass at most every `fullPassMs`), with the balance the
   * attributed changes left (ProfileRecord.econ).
   */
  reconcile: { intervalMs: number; batch: number; fullPassMs: number };
  resources: {
    /** An increase outside the known sources of at least this much is marked (either currency). */
    unattributedMin: CurrencyPair;
    /** ... and is of high severity from this much on (below: medium). */
    unattributedHigh: CurrencyPair;
    /** The window of the income rate check and the most income of the game's own sources in it (medium; 3x: high). */
    rateWindowMs: number;
    maxGameIncome: CurrencyPair;
    /**
     * Spending within `spreeWindowMs` after an unexplained increase of at least `spreeMin` (either currency) of at
     * least `spreeShare` of it is a spree.
     */
    spreeWindowMs: number;
    spreeShare: number;
    spreeMin: CurrencyPair;
    /** An imported (legacy) state with at least this balance is marked (low). */
    importWarn: CurrencyPair;
    /**
     * Repeated identical commands within `windowMs`: one opId answered from the journal `sameOpId` times, one refused
     * command (same op and arguments) `refusedSame` times, opIds reused for other commands `opIdReused` times.
     */
    replays: { windowMs: number; sameOpId: number; refusedSame: number; opIdReused: number };
  };
  battles: {
    /** A battle of at least `fastMinTicks` battle ticks finished in less than ticks / 50 s x `fastRatio` of wall-clock time. */
    fastRatio: number;
    fastMinTicks: number;
    /** -log10 of the model probability of an unbroken streak of new-stage wins (docs/economy.md §1.6) that is marked. */
    streakScore: number;
    /** The mean enemy kills per own turn over the last `battles` qualifying won battles (each >= `minKills` kills). */
    killsPerTurn: { battles: number; minKills: number; mean: number };
    /** Online battles the server's simulation decided against the player (diverged) within `windowMs`: high from `high`. */
    desync: { windowMs: number; high: number };
  };
  /** Evidence items kept per mark (the newest; `count` counts them all). */
  evidenceKept: number;
}

export type DeepPartial<T> = { [K in keyof T]?: T[K] extends readonly unknown[] ? T[K] : T[K] extends object ? DeepPartial<T[K]> : T[K] };

export function defaultConfig(mode: 'in-process' | 'server'): BackendConfig {
  return {
    trustLevel: 'L2',
    allowLegacyImport: mode !== 'server',
    devProfiles: mode !== 'server',
    commands: { journalSize: 64 },
    auth: {
      accessTtlMs: 3600_000,
      refreshTtlMs: 30 * 24 * 3600_000,
      maxSessionsPerAccount: 10,
      singleSession: true,
      refreshReuseGraceMs: 10_000,
      refreshRetryWindowMs: 120_000,
      defaultDisplayName: 'Игрок',
      maxLoginFailures: 10,
      maxLoginFailuresPerAddress: 50,
      loginFailureWindowMs: 15 * 60_000,
    },
    leaderboard: { pageSize: 10, maxPages: 10 },
    assault: {
      zoneCount: 17,
      payments: ASSAULT_ZONE_PAYMENTS,
      tributeIntervalMs: 3600_000,
      clanShareDivisor: 10,
      requireVerifiedBattle: false,
    },
    clans: {
      minLevelToCreate: 5,
      createCooldownMs: 24 * 3600_000,
      renameCooldownMs: 7 * 24 * 3600_000,
      rejoinCooldownMs: 24 * 3600_000,
      maxMembers: 30,
      maxOfficers: 5,
      maxRequestsPerPlayer: 3,
      maxRequestsPerClan: 50,
      historyKept: 200,
      emblems: CLAN_EMBLEMS,
      denylist: [],
    },
    chat: {
      historyGlobal: 200,
      historyClan: 100,
      minLevelToSend: 1,
      burst: 5,
      windowMs: 15_000,
      minIntervalMs: 1_000,
      duplicateWindowMs: 60_000,
      addressBurst: 15,
      subscriptionGraceMs: 2 * 60_000,
      maxMuteMs: 365 * 24 * 3600_000,
      words: [],
    },
    auction: { startPrice: 20, roundMs: 7 * 24 * 3600_000, winnersShown: 50 },
    replays: { maxPerPlayer: 100 },
    battles: { keepMs: 30 * 86_400_000, maxStored: 5000 },
    matchmaking: { levelBucketSize: 10, widenAfterMs: 30_000, ticketTtlMs: 10 * 60_000 },
    rooms: { lobbyTtlMs: 2 * 3600_000, chatBurst: 5, chatWindowMs: 5_000 },
    sessions: {
      turnGraceSec: 15,
      finishGraceMs: 60_000,
      soloTtlMs: 6 * 3600_000,
      maxSoloPerPlayer: 4,
      soloStarts: { burst: 8, perHour: 60 },
      keepEndedMs: 3600_000,
      maxHashTicksKept: 256,
      disconnectGraceMs: 60_000,
      persistIntervalMs: 5_000,
      simulationTimeoutMs: 180_000,
    },
    rateLimit: { enabled: mode === 'server', burst: 200, perSecond: 100, accountsPerIpBurst: 20, accountsPerIpPerHour: 20 },
    idempotency: { ttlMs: 10 * 60_000, maxEntries: 20_000, maxBytes: 64 * 1024 * 1024, persist: mode === 'server' },
    sweepIntervalMs: 1_000,
    api: { serverVersion: APP_VERSION, minClientVersion: null, publicBaseUrl: null, disabledFeatures: [] },
    events: { maxEvents: 1000, retentionMs: 30 * 60_000 },
    store: { devTest: mode !== 'server', voidedPollMs: 6 * 3600_000, consumeRetryMs: 10 * 60_000 },
    ads: { admob: false, devTest: mode !== 'server', ticketTtlMs: 3600_000, maxOpenTickets: 5, keepTicketsMs: 30 * 24 * 3600_000 },
    monetizationAudit: { maxEntries: 200_000, maxAgeMs: 3 * 366 * 24 * 3600_000 },
    email: { linkBaseUrl: null, verifyTtlMs: 24 * 3600_000, resetTtlMs: 3600_000, sendsPerAccountPerHour: 5, sendsPerAddressPerHour: 3, noticesPerAddressPerHour: 10, requestsPerClientPerHour: 20 },
    accountAudit: { maxEntries: 100_000, maxAgeMs: 90 * 24 * 3600_000 },
    integrity: { challengeTtlMs: 5 * 60_000, maxTokenAgeMs: 10 * 60_000 },
    push: { maxDevicesPerAccount: 5, testIntervalMs: 60_000 },
    anticheat: defaultAnticheatConfig(mode === 'server'),
  };
}

/**
 * The problems of a part of AnticheatConfig (ANTICHEAT_CONFIG_FILE): unknown keys, values of another type than the
 * default's, numbers that are not finite and >= 0, a share outside 0..1, a reconciliation batch or a kill-rate window
 * below 1. Empty: usable.
 */
export function anticheatConfigProblems(patch: unknown): string[] {
  const out: string[] = [];
  const walk = (v: unknown, base: unknown, path: string): void => {
    if (isObj(base)) {
      if (!isObj(v)) {
        out.push(`${path || 'the file'} must be an object`);
        return;
      }
      for (const [k, x] of Object.entries(v)) {
        if (!Object.prototype.hasOwnProperty.call(base, k)) out.push(`${path ? `${path}.` : ''}${k} is not a setting of the anti-cheat`);
        else walk(x, (base as Record<string, unknown>)[k], path ? `${path}.${k}` : k);
      }
      return;
    }
    if (typeof base === 'boolean' && typeof v !== 'boolean') out.push(`${path} must be true or false`);
    if (typeof base === 'number' && (typeof v !== 'number' || !Number.isFinite(v) || v < 0)) out.push(`${path} must be a number >= 0`);
    if (path.endsWith('spreeShare') && typeof v === 'number' && v > 1) out.push(`${path} must be at most 1`);
    // A batch of 0 would never finish the full pass; a window of 0 battles would keep every battle's kill rate.
    if ((path === 'reconcile.batch' || path === 'battles.killsPerTurn.battles') && typeof v === 'number' && v < 1) out.push(`${path} must be at least 1`);
  };
  walk(patch, defaultAnticheatConfig(true), '');
  return out;
}

/** The anti-cheat's defaults (the reasoning behind each value: docs/security.md). */
export function defaultAnticheatConfig(enabled: boolean): AnticheatConfig {
  return {
    enabled,
    reconcile: { intervalMs: 60_000, batch: 200, fullPassMs: 24 * 3600_000 },
    resources: {
      unattributedMin: { gold: 1, diam: 1 },
      unattributedHigh: { gold: 100_000, diam: 100 },
      rateWindowMs: 3600_000,
      // The best honest hour: the five largest bosses (14-18) won one after another on the first attempt at the highest
      // difficulty give 10.7 million gold (12.9 million with the champion perk), the tribute at most 0.8 million.
      maxGameIncome: { gold: 20_000_000, diam: 300 },
      spreeWindowMs: 3600_000,
      spreeShare: 0.5,
      spreeMin: { gold: 10_000, diam: 10 },
      importWarn: { gold: 10_000_000, diam: 10_000 },
      replays: { windowMs: 10 * 60_000, sameOpId: 20, refusedSame: 30, opIdReused: 3 },
    },
    battles: {
      fastRatio: 0.75,
      fastMinTicks: 1_000,
      streakScore: 20,
      killsPerTurn: { battles: 10, minKills: 4, mean: 5 },
      desync: { windowMs: 7 * 24 * 3600_000, high: 3 },
    },
    evidenceKept: 20,
  };
}

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function mergeConfig<T>(base: T, patch: DeepPartial<T> | undefined): T {
  if (!patch) return base;
  const out: Record<string, unknown> = { ...(base as Record<string, unknown>) };
  for (const [k, v] of Object.entries(patch as Record<string, unknown>)) {
    if (v === undefined) continue;
    out[k] = isObj(v) && isObj(out[k]) ? mergeConfig(out[k], v as DeepPartial<unknown>) : v;
  }
  return out as T;
}
